import { useEffect, useMemo, useRef } from 'react';
import { useDebounce } from 'hooks/useDebounced';
import { decodeFilterState, encodeFilterState } from 'utils/filters/urlFilterState';

interface UseFilterUrlSyncParams<T extends Record<string, unknown>> {
  /** Query param key the compressed filter blob lives under (e.g. `f`). */
  paramKey: string;
  /**
   * Current filter selection as a compact object. Callers should omit empty
   * values (empty strings/arrays, null) so an inactive filter set produces an
   * empty object and the param is removed from the URL.
   */
  state: T;
  /** Apply a decoded filter object back onto the page's atoms on hydration. */
  apply: (state: T) => void;
  /** Optional debounce (ms) for URL writes, e.g. while typing a keyword. */
  debounceMs?: number;
}

const readParam = (key: string): string | null =>
  new URLSearchParams(window.location.search.slice(1)).get(key);

const writeParam = (key: string, value: string) => {
  const params = new URLSearchParams(window.location.search.slice(1));
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
  const queryString = params.toString();
  window.history.replaceState(
    null,
    '',
    queryString ? `?${queryString}` : window.location.pathname,
  );
  // Keep any URL-backed atoms (see state/queryParams.tsx) in sync with our write.
  window.dispatchEvent(new Event('searchchange'));
};

/**
 * Two-way sync between a page's filter atoms and a single compressed URL param.
 *
 * - On mount: reads `paramKey`, decodes it, and calls `apply` to restore filters
 *   from a shared/refreshed link.
 * - On change: writes the compressed `state` back to `paramKey` (via
 *   `history.replaceState`, so no browser-history spam), or removes the param
 *   when no filters are active.
 *
 * Generic across pages — each page supplies its own `state`/`apply` mapping.
 */
export const useFilterUrlSync = <T extends Record<string, unknown>>({
  paramKey,
  state,
  apply,
  debounceMs = 0,
}: UseFilterUrlSyncParams<T>) => {
  const hydratedRef = useRef(false);

  // Hydrate filters from the URL exactly once, on mount.
  useEffect(() => {
    const decoded = decodeFilterState<T>(readParam(paramKey));
    if (decoded) {
      apply(decoded);
    }
    hydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stateKey = JSON.stringify(state);
  const encoded = useMemo(() => {
    if (!state || Object.keys(state).length === 0) {
      return '';
    }

    return encodeFilterState(state);
    // `state` is keyed by its serialized form so we only recompress on change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey]);

  const debouncedEncoded = useDebounce(encoded, debounceMs);

  // Write filters back to the URL whenever they change (after hydration).
  useEffect(() => {
    if (!hydratedRef.current) {
      return;
    }
    writeParam(paramKey, debouncedEncoded);
  }, [paramKey, debouncedEncoded]);

  return { isHydrated: () => hydratedRef.current };
};
